import {useState, useEffect, useCallback} from '@wordpress/element';
import {getSetting} from '@woocommerce/settings';
import {isProduction} from '@ppcp/utils';

export const usePaymentsClient = (
    {
        paypal,
        onPaymentAuthorized,
        onPaymentDataChanged,
        needsShipping = false
    }) => {
    const [paymentsClient, setPaymentsClient] = useState(null);
    const [googlePayConfig, setGooglePayConfig] = useState(null);
    const [error, setError] = useState(null);
    const data = getSetting('ppcp_googlepay_data');

    const createPaymentsClient = useCallback(async () => {
        try {
            if (!paypal?.Googlepay) {
                throw new Error('Google Pay SDK not loaded');
            }

            // Get Google Pay configuration from PayPal SDK
            const config = await paypal.Googlepay().config();
            setGooglePayConfig(config);

            // Build payments client request
            const {merchantInfo} = config;
            const clientRequest = {
                environment: isProduction() ? 'PRODUCTION' : 'TEST',
                merchantInfo,
                paymentDataCallbacks: {
                    onPaymentAuthorized: onPaymentAuthorized,
                    ...(needsShipping && onPaymentDataChanged && {
                        onPaymentDataChanged: onPaymentDataChanged
                    })
                }
            };

            // Create Google Pay payments client
            const client = new google.payments.api.PaymentsClient(clientRequest);
            setPaymentsClient(client);

            return client;
        } catch (err) {
            console.error('Failed to create Google Pay payments client:', err);
            setError(err);
            return null;
        }
    }, [paypal, data, onPaymentAuthorized, onPaymentDataChanged, needsShipping]);

    useEffect(() => {
        if (paypal && !paymentsClient) {
            createPaymentsClient();
        }
    }, [
        paypal,
        paymentsClient,
        createPaymentsClient
    ]);

    return {
        error,
        paymentsClient,
        googlePayConfig
    };
}